import axios from "axios";
import {
  getStoredToken,
  isTokenExpired,
  clearAuthAndRedirectToLogin,
} from "../utils/auth";

const WABA_API_BASE = process.env.REACT_APP_WABA_SERVER_URL;

const callsAPI = axios.create({
  baseURL: `${WABA_API_BASE}/api/calls`,
  headers: { "Content-Type": "application/json" },
});

callsAPI.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (!token || isTokenExpired(token)) {
    clearAuthAndRedirectToLogin();
    return Promise.reject(new Error("Token expired"));
  }
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

callsAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAuthAndRedirectToLogin();
    }
    return Promise.reject(error);
  }
);

/** Get call settings for a phone. */
export const getCallSettings = (phoneId) =>
  callsAPI.get(`/settings/${phoneId}`);

/** Update call settings — pass Meta-shaped `calling` object. */
export const updateCallSettings = (phoneId, calling) =>
  callsAPI.put(`/settings/${phoneId}`, { calling });

/** Get the call permission state for a user number. */
export const getCallPermission = (phoneId, userNumber) =>
  callsAPI.get(`/permission/${phoneId}`, { params: { userNumber } });

/** Send a call permission request message to a user (1/24h, 2/7d limits). */
export const requestCallPermission = (phoneId, to, bodyText) =>
  callsAPI.post("/permission-request", { phone_id: phoneId, to, body_text: bodyText });

/** Get the account's call-permission-request template (if any). */
export const getPermissionTemplate = (phoneId) =>
  callsAPI.get(`/permission-template/${phoneId}`);

/** Create a default call-permission-request template (needs Meta approval). */
export const createPermissionTemplate = (phoneId, bodyText) =>
  callsAPI.post("/permission-template", { phone_id: phoneId, body_text: bodyText });

/** Start an outbound call (SDP offer from the browser). */
export const initiateCall = (phoneId, to, sdpOffer) =>
  callsAPI.post("/initiate", { phone_id: phoneId, to, sdp_offer: sdpOffer });

/** Answer an inbound call (SDP answer from the browser). */
export const acceptCall = (phoneId, callId, sdpAnswer) =>
  callsAPI.post("/accept", { phone_id: phoneId, call_id: callId, sdp_answer: sdpAnswer });

/** Decline an inbound ringing call. */
export const rejectCall = (phoneId, callId) =>
  callsAPI.post("/reject", { phone_id: phoneId, call_id: callId });

/** Hang up a live call. */
export const terminateCall = (phoneId, callId) =>
  callsAPI.post("/terminate", { phone_id: phoneId, call_id: callId });

/** Call history for the account (optionally per phone). */
export const getCallHistory = (params = {}) =>
  callsAPI.get("/history", { params });
